
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { type Question, type Answer, QuestionnaireService } from '../../../types/questionnaire';
import useTrackEvent from '../../../utils/trackEvent';
import ThemeComponent from '@/components/MobileVariant/Themes';
import PreThemeInfo from '@/components/MobileVariant/PreThemeInfo';
import QuestionWrapper from '@/components/MobileVariant/QuestionWrapper';
import PreQuestionnaireInfo from '@/components/MobileVariant/PreQuestionnaireInfo';
import { compressToEncodedURIComponent } from 'lz-string';
import Summary from '@/components/MobileVariant/Summary';
import { Styling } from '@/types/customer';

interface SelectedTheme {
    theme: Answer,
    completed: boolean,
    questionCount: number,
}

function Form() {
    const THEME_INDEX_THEME_SELECTION: number = -1;
    const THEME_INDEX_INSTRUCTIONS: number = -2;
    const QUESTION_INDEX_INFO: number = -1;

    const navigateTo = useNavigate();
    const trackEvent = useTrackEvent();

    const getQuestionnaire = () => window.customer.questionnaireData;

    const getCurrentQuestionContexts: () => Answer[] = () => {
        if( currentTheme === null ) return [];
        if( currentTheme.theme.questions == null ) return [];
        return currentTheme.theme.questions[0].answers;
    }

    const getCurrentQuestion: () => Question|null = () => {
        const contexts: Answer[] = currentQuestionContexts;
        
        if( questionIndex < 0 || questionIndex >= contexts.length ) return null;
        const context: Answer = contexts[questionIndex];
        if( context.questions == null ) return null;
        return context.questions[0] ?? null;
    }

    const [questionnaire, setQuestionnaire] = useState<Question>(getQuestionnaire() ?? {
        id: -1, title: "", type: "themes", answers: []
    });

    const [selectedThemes, setSelectedThemes] = useState<SelectedTheme[]>([]);
    const [completedThemes, setCompletedThemes] = useState<{[key: number]: Answer}>({});
    // current theme index. Special indices: -1: theme select screen. -2: 'welcome/instruction' screen
    const [themeIndex, setThemeIndex] = useState<number>(THEME_INDEX_INSTRUCTIONS);
    const [questionIndex, setQuestionIndex] = useState<number>(QUESTION_INDEX_INFO);
    const [selectionCount, setSelectionCount] = useState<number>(0);
    
    const currentTheme: SelectedTheme|null = themeIndex >= 0 && themeIndex < selectedThemes.length ? selectedThemes[themeIndex] : null;
    const currentQuestionContexts: Answer[] = getCurrentQuestionContexts();
    const currentQuestion: Question|null = getCurrentQuestion();

    const [currentQuestionCompleted, setCurrentQuestionComplete] = useState<boolean>(false);
/***** EVENTS *****/
    const onClickPreviousQuestion = () => {
        decreaseQuestionaireIndices();
    }

    const onClickNextQuestion = () => {
        if( !isButtonNextEnabled() ) return;

        if( themeIndex >= selectedThemes.length ) {
            submitAndGoToResultPage();
            return;
        }
        // check if we're at the theme selection screen.
        // otherwise, update the selected answers
        const doContinue: boolean = themeIndex === THEME_INDEX_THEME_SELECTION ? updateThemeSelection() : submitAnswers();
        // then show the next question
        if( doContinue && questionIndex <= currentQuestionContexts.length ) {
            increaseQuestionnaireIndices();
        }
    }

    useEffect(() => {
        const q: Question|null = themeIndex <= THEME_INDEX_THEME_SELECTION ? questionnaire : currentQuestion;
        const completed: boolean = QuestionnaireService.QuestionIsCompleted(q, themeIndex != THEME_INDEX_THEME_SELECTION)
        setCurrentQuestionComplete(completed);
    }, [questionIndex, themeIndex])

    useEffect(() => {
        if( currentQuestionContexts && currentQuestionContexts[questionIndex] )
            currentQuestionContexts[questionIndex].checked = currentQuestionCompleted;
    }, [currentQuestionCompleted])

/***** QUESTIONNAIRE LOGIC *****/
    const updateThemeSelection: () => boolean = () => {
        if( questionnaire === null ) return false;

        const newThemes: SelectedTheme[] = [];
        const answers: Answer[] = questionnaire.answers;
        for(const answer of answers) {
            if( !answer.checked ) continue;
            newThemes.push({
                theme: answer,
                completed: false,
                questionCount: QuestionnaireService.ThemeGetQuestionCount(answer),
            });
        }
        setSelectedThemes([...newThemes]);

        return true;
    }

    const submitAnswers: () => boolean = () => {
        return true;
    }

    const increaseQuestionnaireIndices = () => {
        if( questionIndex < currentQuestionContexts.length - 1 ) {
            setQuestionIndex(questionIndex + 1);
            setCurrentQuestionComplete(false);
            return;
        }
        if( themeIndex < selectedThemes.length ) {
            setThemeIndex(themeIndex + 1)
            setCurrentQuestionComplete(false);
            setQuestionIndex(QUESTION_INDEX_INFO);
        }
    }

    const decreaseQuestionaireIndices = () => {
        // if we're still showing a question, just decrease the question index
        if( questionIndex >= 0 ) {
            setQuestionIndex(questionIndex - 1);
            return;
        }
        // if there are still previous teams, update the question index to the previous themes last question
        if( themeIndex > 0 ) {
            const prevTheme: SelectedTheme = selectedThemes[themeIndex - 1];
            setQuestionIndex(QuestionnaireService.ThemeGetQuestionCount(prevTheme.theme) - 1);
            setThemeIndex(themeIndex - 1);
            return;
        }
        // otherwise, just reset the indices to -1/-1
        if( themeIndex > THEME_INDEX_INSTRUCTIONS ) {
            setThemeIndex(themeIndex - 1);
            setQuestionIndex(QUESTION_INDEX_INFO);
        }
    }

    const redoQuestionnaire = () => {
        // mark completed themes
        const currentlyCompletedThemes: {[key: number]: Answer} = {... completedThemes};
        // NOTE: this assumes we fully complete each theme
        for(const selectedTheme of selectedThemes) {
            currentlyCompletedThemes[selectedTheme.theme.id] = selectedTheme.theme;
            selectedTheme.theme.checked = false;
        }
        // reset indices
        setCompletedThemes(currentlyCompletedThemes);
        setThemeIndex(THEME_INDEX_THEME_SELECTION);
        setQuestionIndex(QUESTION_INDEX_INFO);
    }
/***** SUBMITTING *****/
    const submitAndGoToResultPage = () => {
        // ensure all completed themes are checked
        for(const themeId in completedThemes) {
            completedThemes[themeId].checked = true;
        }
        const {groupedData, response, urlParams} = QuestionnaireService.createQuestionnaireSubmission(questionnaire, selectedThemes.map((val: SelectedTheme) => val.theme), trackEvent);
        const compressedParams = compressToEncodedURIComponent(
          urlParams + '&groupedAnswers=' + encodeURIComponent(JSON.stringify(groupedData))
        );
        navigateTo(`/results?data=${compressedParams}`);
      };
/***** NEXT/PREV BUTTON LOGIC *****/
    const isButtonNextEnabled: () => boolean = () => {
    //    if( themeIndex <= THEME_INDEX_INSTRUCTIONS ) return true;
        const completedAnyThemes: boolean = Object.keys(completedThemes).length > 0;
        if( themeIndex == THEME_INDEX_THEME_SELECTION && !currentQuestionCompleted && !completedAnyThemes ) return false;
        return true;
    }
/***** DYNAMIC LABELS ******/
    const labelButtonNext: () => string = () => {
        if( themeIndex === THEME_INDEX_THEME_SELECTION ) {
            const completedAnyThemes: boolean = Object.keys(completedThemes).length > 0;
            if( completedAnyThemes ) {
                if( !currentQuestionCompleted ) {
                    return "Terug naar Overzicht";
                } else {
                    return "Volgende"
                }
            }
            return "Starten";
        }
        if( themeIndex >= selectedThemes.length ) return "Naar advies";
        return "Volgende";
    }
/***** DEBUGGING AND LOGGING *****/

/***** RENDERING *****/
    let backgroundGradientClass: string = "";
    let renderComp = <>
    <div>Something went wrong</div>
    <div>TI: { themeIndex } QI: { questionIndex }</div>
    </>;
    if( themeIndex <= THEME_INDEX_INSTRUCTIONS ) {
        renderComp = <PreQuestionnaireInfo/>
        backgroundGradientClass = 'back-gradient-short';
    } else if( themeIndex <= THEME_INDEX_THEME_SELECTION ) {
        renderComp = <ThemeComponent questionnaire={questionnaire} completedThemes={completedThemes} setQuestionCompleted={setCurrentQuestionComplete}/>;
    } else if( themeIndex >= selectedThemes.length ) {
        backgroundGradientClass = 'back-gradient-short';
        renderComp = <Summary questionnaire={questionnaire} completedThemes={completedThemes} redoQuestionnaire={redoQuestionnaire}/>
    } else if( currentTheme != null && questionIndex < 0 ) {
        renderComp = <PreThemeInfo theme={ currentTheme.theme } themeIndex={ themeIndex + 1 } questionCount={ currentTheme.questionCount }/>
        backgroundGradientClass = 'back-gradient-short';
    } else if( currentQuestionContexts[questionIndex] || currentQuestion ) {
        renderComp = <QuestionWrapper
            context={currentQuestionContexts[questionIndex]} 
            question={ currentQuestion }
            questionIndex={ questionIndex }
            questionTotal={ currentQuestionContexts.length }
            setQuestionCompleted={ setCurrentQuestionComplete }
            themeName={currentTheme && currentTheme.theme.title ? currentTheme.theme.title : "Onbekend Thema"}
            themeIndex={themeIndex+1}
            themeTotalSelected={selectedThemes.length}
        />
    } 

    return (
        <>
        <div style={{"display": "none"}}>
            <svg>
                <defs>
                    <symbol id="uv-checkbox-mark" width="16" height="12" viewBox="0 0 16 12" fill="none">
                        <path d="M5.29845 11.4046L0.228447 6.33463C-0.076149 6.03003 -0.076149 5.53616 0.228447 5.23154L1.33151 4.12845C1.6361 3.82382 2.13 3.82382 2.4346 4.12845L5.84999 7.54381L13.1654 0.228447C13.47 -0.076149 13.9639 -0.076149 14.2685 0.228447L15.3715 1.33154C15.6761 1.63613 15.6761 2.13 15.3715 2.43463L6.40154 11.4047C6.09691 11.7093 5.60304 11.7093 5.29845 11.4046Z" fill={ Styling.PrimaryColorDark() }/>
                    </symbol>
                    <symbol id="uv-complete-mark" width="13" height="10" viewBox="0 0 13 10" fill="none">
                        <path d="M4.41537 9.50386L0.190373 5.27886C-0.0634575 5.02503 -0.0634575 4.61347 0.190373 4.35961L1.10959 3.44037C1.36342 3.18652 1.775 3.18652 2.02883 3.44037L4.87499 6.28651L10.9712 0.190373C11.225 -0.0634575 11.6366 -0.0634575 11.8904 0.190373L12.8096 1.10961C13.0634 1.36344 13.0634 1.775 12.8096 2.02886L5.33462 9.50388C5.08076 9.75771 4.6692 9.75771 4.41537 9.50386Z" fill="white"/>
                    </symbol>
                </defs>
            </svg>
        </div>
        <div className={"background-header variant-mobile" + (backgroundGradientClass.length > 0 ? (" " + backgroundGradientClass) : "" )}>
            <div className="variant-wrapper">
                <div className="site-container h-full">
                    <div className="sections space-y-5 h-full">
                        { renderComp }
                    </div>
                </div>
                <div className="controls flex flex-row gap-4">
                    <button
                        className="btn btn-primary-outline flex-1 font-bold"
                        onClick={ onClickPreviousQuestion }
                        disabled= { themeIndex <= THEME_INDEX_INSTRUCTIONS }
                    >
                        <i className="fas fa-chevron-left"></i>
                        Vorige
                    </button>
                    <button
                        className="btn btn-secondary flex-1"
                        onClick= { onClickNextQuestion }
                        disabled={ !isButtonNextEnabled() }
                    >
                        { labelButtonNext() }
                        <i className="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>
        
        </>
    )
}

export default Form;